'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_URL } from '@/lib/api';
import { useRealtimeStream } from '@/lib/hooks/useRealtimeStream';

export type UserRole = 'admin' | 'manager' | 'technicien' | 'dentiste';

export interface AuthUser {
  id: number | string;
  name?: string;
  email: string;
  role: UserRole;
  /** Permissions effectives du rôle (matrice configurable dans /settings). */
  permissions?: string[];
  permissions_version?: string;
  /** Entrées de menu masquées pour ce rôle (config « Affichage du menu » /settings). */
  menu_hidden?: string[];
  [key: string]: unknown;
}

interface AuthContextValue {
  user: AuthUser | null;
  /** Jeton Sanctum renvoyé au login (mémoire uniquement, non persistant). */
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Permissions effectives de l'utilisateur connecté. */
  permissions: string[];
  /** Vrai si l'utilisateur dispose de la permission (l'admin a toujours tout). */
  can: (permission: string) => boolean;
  login: (email: string, password: string) => Promise<{ token: string; user: AuthUser }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Recopie l'empreinte des droits dans un cookie lisible par le middleware Next.
 * Celui-ci met la session en cache par jeton ; sans cette empreinte dans la clé, il
 * continuerait à autoriser/refuser avec d'anciens droits jusqu'à expiration du cache.
 */
const PERMISSIONS_VERSION_COOKIE = 'bdl_perm_v';

function syncPermissionsVersionCookie(user: AuthUser | null): void {
  if (typeof document === 'undefined') return;
  const version = user && typeof user.permissions_version === 'string' ? user.permissions_version : '';
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = version
    ? `${PERMISSIONS_VERSION_COOKIE}=${encodeURIComponent(version)}; path=/; SameSite=Lax; Max-Age=86400${secure}`
    : `${PERMISSIONS_VERSION_COOKIE}=; path=/; SameSite=Lax; Max-Age=0${secure}`;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  const parseAuthPayload = (json: unknown): { token: string; user: AuthUser } => {
    const root = (
      json && typeof json === 'object' ? (json as Record<string, unknown>) : {}
    ) as Record<string, unknown>;
    const maybeData = root.data;
    const payload =
      maybeData && typeof maybeData === 'object'
        ? (maybeData as Record<string, unknown>)
        : (root as Record<string, unknown>);
    const nextToken = payload.token;
    const nextUser = payload.user;
    if (typeof nextToken !== 'string' || !nextToken || !nextUser || typeof nextUser !== 'object') {
      throw new Error('Réponse API invalide');
    }
    return { token: nextToken, user: nextUser as AuthUser };
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const res = await fetch(`${API_URL}/auth/me`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        });
        if (!res.ok) {
          if (res.status === 401) {
            await fetch(`${API_URL}/auth/forgot-session-cookie`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: '{}',
            });
          }
          setUser(null);
          setToken(null);
          setIsLoading(false);
          return;
        }
        const json = await res.json();
        const me = (json?.data ?? json) as AuthUser;
        setUser(me);
        setToken(null);
      } catch {
        setUser(null);
        setToken(null);
      } finally {
        setIsLoading(false);
      }
    };

    void bootstrap();
  }, []);

  /**
   * Recharge le profil (rôle, permissions, entrées de menu masquées) depuis /auth/me.
   * Appelé par le tick de session, au retour de focus, et surtout à la réception d'un
   * événement realtime « access » quand l'admin change la matrice dans /settings.
   */
  const refreshProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const json = await res.json();
      const me = (json?.data ?? json) as AuthUser;
      if (me && typeof me === 'object' && me.id !== undefined) {
        setUser((prev) => (JSON.stringify(prev) === JSON.stringify(me) ? prev : me));
      }
    } catch {
      /* erreur réseau : le tick de session réessaiera */
    }
  }, []);

  // Droits d'accès poussés en temps réel : dès que l'admin enregistre la matrice de
  // permissions ou l'affichage du menu, le backend publie sur le topic « access » et
  // chaque onglet ouvert recharge son profil — sans attendre le tick de 30 s.
  useRealtimeStream({
    topics: user ? ['access'] : [],
    onEvent: (event) => {
      if (event.type === 'sync') return;
      void refreshProfile();
    },
  });

  // Déconnexion automatique si la session est révoquée côté serveur
  // (ex. : un dentiste passé « inactif » voit ses tokens supprimés) — sans
  // attendre que l'utilisateur rafraîchisse ou déclenche une action.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const checkSession = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await fetch(`${API_URL}/auth/me`, {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (cancelled) return;
        if (res.ok) {
          // Rafraîchit le profil (permissions + entrées de menu) sans rechargement
          // manuel : un changement fait par l'admin dans /settings est pris en compte
          // au prochain tick (30s) ou dès que l'onglet reprend le focus.
          try {
            const json = await res.json();
            const me = (json?.data ?? json) as AuthUser;
            if (!cancelled && me && typeof me === 'object' && me.id !== undefined) {
              setUser((prev) => (JSON.stringify(prev) === JSON.stringify(me) ? prev : me));
            }
          } catch {
            /* corps illisible : on ignore, la session reste valide */
          }
          return;
        }
        if (res.status === 401 || res.status === 403) {
          await fetch(`${API_URL}/auth/forgot-session-cookie`, {
            method: 'POST',
            credentials: 'include',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: '{}',
          }).catch(() => {});
          setUser(null);
          setToken(null);
          if (typeof window !== 'undefined') {
            window.location.assign('/login?reason=session_ended');
          }
        }
      } catch {
        /* erreur réseau : on ignore et on réessaiera au prochain tick */
      }
    };

    const interval = setInterval(() => void checkSession(), 30_000);
    const onVisible = () => {
      if (typeof document !== 'undefined' && !document.hidden) void checkSession();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const json = (await res.json().catch(() => ({}))) as unknown;
    if (!res.ok) {
      const root = (json && typeof json === 'object' ? json : {}) as Record<string, unknown>;
      const message: string =
        typeof root.message === 'string' ? root.message : 'Erreur de connexion';
      // Code stable renvoyé par l'API (ex. 'pending_approval') pour piloter l'affichage
      // du badge sur la page Connexion — plus robuste qu'un test sur le texte du message.
      const data = (root.data && typeof root.data === 'object' ? root.data : {}) as Record<
        string,
        unknown
      >;
      const reason = typeof data.reason === 'string' ? data.reason : undefined;
      const error = new Error(message) as Error & { reason?: string; status?: number };
      error.reason = reason;
      error.status = res.status;
      throw error;
    }

    const payload = parseAuthPayload(json);
    setToken(payload.token);
    setUser(payload.user);
    return payload;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: '{}',
      });
    } finally {
      setUser(null);
      setToken(null);
    }
  }, []);

  // Le cookie suit l'utilisateur courant : bootstrap, refresh temps réel, login, logout.
  useEffect(() => {
    syncPermissionsVersionCookie(user);
  }, [user]);

  const permissions = useMemo<string[]>(
    () => (Array.isArray(user?.permissions) ? (user!.permissions as string[]) : []),
    [user]
  );

  const can = useCallback(
    (permission: string) => {
      if (!user) return false;
      // L'admin a toujours toutes les permissions (anti-verrouillage, aligné backend).
      if (user.role === 'admin') return true;
      return permissions.includes(permission);
    },
    [user, permissions]
  );

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      token,
      isLoading,
      isAuthenticated,
      permissions,
      can,
      login,
      logout,
    }),
    [user, token, isLoading, isAuthenticated, permissions, can, login, logout]
  );

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:310:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="310" data-component-end-line="310" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
};
